// src/utils/transactionalTemplates.js
// Single source of truth for the auto-triggered (transactional) email + push notification templates.
//
// Admin edits are stored on the salon document in Firestore (not localStorage) so that every admin, browser,
// the Bookings AI agent and the Cloud Functions all send the same wording:
//   salons/{salonId}.emailTemplates.<templateId> = { subject, body, html }
//   salons/{salonId}.pushTemplates.<templateId>  = { title, body }
//   salons/{salonId}.transactionalCampaignSettings.<email_|push_><templateId> = false  (disabled)
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export const DEFAULT_TIMEZONE = 'Asia/Singapore';

// Template ids that are sent automatically and therefore read from the salon document at send time.
export const AUTO_EMAIL_TEMPLATE_IDS = ['appointment_confirmation', 'appointment_rejected', 'payment_receipt', 'loyalty_points_earned'];
export const AUTO_PUSH_TEMPLATE_IDS = [
  'appointment_reminder', 'appointment_approved', 'appointment_rejected', 'payment_confirmation', 'loyalty_update',
  'wallet_debit', 'wallet_topup', 'wallet_topup_rejected'
];

export const DEFAULT_EMAIL_TEMPLATES = {
  appointment_confirmation: {
    subject: 'Appointment Confirmed - See You Soon! 📅',
    html: `<p>Hi {name}!</p>
<p><strong>Your appointment has been confirmed!</strong></p>
<p>📅 <strong>Date:</strong> {date}<br>
⏰ <strong>Time:</strong> {time}<br>
💇‍♀️ <strong>Service:</strong> {service}<br>
👤 <strong>Staff:</strong> {staff}</p>
<p>We look forward to pampering you! If you need to reschedule, please give us a call.</p>
<p>See you soon!</p>`,
    body: `Hi {name}!\n\nYour appointment has been confirmed!\n\n📅 Date: {date}\n⏰ Time: {time}\n💇‍♀️ Service: {service}\n👤 Staff: {staff}\n\nWe look forward to pampering you! If you need to reschedule, please give us a call.\n\nSee you soon!`
  },
  appointment_rejected: {
    subject: 'Appointment Update - Kindly Book Again',
    html: `<p>Hi {name}!</p>
<p>Thank you for your booking request. Unfortunately, we are unable to accommodate your appointment at the requested time.</p>
<p>📅 <strong>Date:</strong> {date}<br>
⏰ <strong>Time:</strong> {time}<br>
💇‍♀️ <strong>Service:</strong> {service}<br>
👤 <strong>Staff:</strong> {staff}</p>
<p>We sincerely apologise for the inconvenience. Please visit our booking page to select another available date and time that works for you.</p>
<p>We look forward to welcoming you soon!</p>`,
    body: `Hi {name}!\n\nThank you for your booking request. Unfortunately, we are unable to accommodate your appointment at the requested time.\n\n📅 Date: {date}\n⏰ Time: {time}\n💇‍♀️ Service: {service}\n👤 Staff: {staff}\n\nWe sincerely apologise for the inconvenience. Please visit our booking page to select another available date and time that works for you.\n\nWe look forward to welcoming you soon!`
  },
  payment_receipt: {
    subject: 'Payment Receipt - Thank You! 💳',
    html: `<p>Hi {name}!</p>
<p><strong>Thank you for your payment!</strong></p>
<p>💳 <strong>Amount Paid:</strong> {amount}<br>
📧 <strong>Invoice:</strong> #{invoice_number}</p>
<p>We appreciate your business and look forward to seeing you at your next appointment!</p>
<p>Thank you for choosing us!</p>`,
    body: `Hi {name}!\n\nThank you for your payment!\n\n💳 Amount Paid: {amount}\n📧 Invoice: #{invoice_number}\n\nWe appreciate your business and look forward to seeing you at your next appointment!\n\nThank you for choosing us!`
  },
  loyalty_points_earned: {
    subject: 'You\'ve Earned Loyalty Points! ⭐',
    html: `<p>Hi {name}!</p>
<p><strong>Great news! You've earned loyalty points!</strong></p>
<p>⭐ <strong>Points Earned:</strong> {points_earned}<br>
💎 <strong>Total Balance:</strong> {total_points} points</p>
<p>Keep collecting points and enjoy exclusive rewards! Every visit brings you closer to something special.</p>
<p>Thank you for being a valued customer!</p>`,
    body: `Hi {name}!\n\nGreat news! You've earned loyalty points!\n\n⭐ Points Earned: {points_earned}\n💎 Total Balance: {total_points} points\n\nKeep collecting points and enjoy exclusive rewards! Every visit brings you closer to something special.\n\nThank you for being a valued customer!`
  }
};

export const DEFAULT_PUSH_TEMPLATES = {
  appointment_reminder: {
    title: 'Appointment {day} 🗓️',
    body: 'Hi {name}! Reminder: {service} {day} at {time} with {staff}'
  },
  appointment_approved: {
    title: 'Appointment Confirmed! ✅',
    body: 'Great news! Your {service} appointment on {date} at {time} with {staff} has been approved. See you soon!'
  },
  appointment_rejected: {
    title: 'Appointment Update',
    body: 'Your {service} appointment request on {date} at {time} with {staff} could not be accommodated. Please rebook at another time.'
  },
  payment_confirmation: {
    title: 'Payment Received ✅',
    body: 'Thank you! Your payment of {amount} has been processed'
  },
  loyalty_update: {
    title: 'Points Earned! 🌟',
    body: 'You earned {points} points! Total: {total_points} points'
  },
  wallet_debit: {
    title: 'Wallet Debited',
    body: 'Your wallet was debited {amount} for {item}. New balance: {balance}.'
  },
  wallet_topup: {
    title: 'Wallet Top-Up Verified',
    body: 'Your {amount} top-up has been verified. New balance: {balance}.'
  },
  wallet_topup_rejected: {
    title: 'Wallet Top-Up Update',
    body: 'Your {amount} wallet top-up request could not be verified. Please contact us if you believe this is an error.'
  }
};

// Aliases accepted by older / editor-default wording, so every placeholder an admin may have typed resolves.
const ALIASES = {
  service_name: 'service',
  points: 'points_earned',
  points_earned: 'points',
  staff_name: 'staff',
  customer_name: 'name',
  invoice: 'invoice_number'
};

/**
 * Replace {placeholders} in text. "${amount}" is treated as "{amount}" because the value already carries "$".
 * Unknown placeholders are left untouched so a typo is visible rather than silently blank.
 */
export const renderTemplate = (text, vars = {}) => {
  if (typeof text !== 'string') return '';
  const lookup = (key) => {
    if (vars[key] !== undefined && vars[key] !== null) return String(vars[key]);
    const alias = ALIASES[key];
    if (alias && vars[alias] !== undefined && vars[alias] !== null) return String(vars[alias]);
    return null;
  };
  return text
    .replace(/\$\{(amount)\}/g, (m, k) => lookup(k) ?? m)
    .replace(/\{(\w+)\}/g, (m, k) => lookup(k) ?? m);
};

export const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDateInTz = (date, timeZone = DEFAULT_TIMEZONE) =>
  date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone });

export const formatTimeInTz = (date, timeZone = DEFAULT_TIMEZONE) =>
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone });

export const formatMoney = (amount) => `$${(parseFloat(amount) || 0).toFixed(2)}`;

// Friendly, stable invoice reference derived from the transaction document id (never shown as the raw id).
export const makeInvoiceNumber = (transactionId) =>
  `INV-${String(transactionId || '').slice(-8).toUpperCase()}`;

/**
 * Load everything needed to send a transactional notification for a salon in one read.
 * On failure everything defaults to "enabled, default templates" so a Firestore hiccup never blocks the booking.
 */
export const loadSalonNotificationConfig = async (salonId) => {
  const empty = { settings: {}, emailTemplates: {}, pushTemplates: {}, timeZone: DEFAULT_TIMEZONE };
  if (!salonId) return empty;
  try {
    const snap = await getDoc(doc(db, 'salons', salonId));
    if (!snap.exists()) return empty;
    const d = snap.data();
    return {
      settings: d.transactionalCampaignSettings || {},
      emailTemplates: d.emailTemplates || {},
      pushTemplates: d.pushTemplates || {},
      timeZone: (d.settings && d.settings.timezone) || DEFAULT_TIMEZONE
    };
  } catch (e) {
    console.warn('Could not load salon notification config, using defaults:', e);
    return empty;
  }
};

export const resolveEmailTemplate = (cfg, id) => {
  const def = DEFAULT_EMAIL_TEMPLATES[id];
  const custom = cfg.emailTemplates[id];
  if (!custom) return def;
  return {
    subject: custom.subject || def.subject,
    html: custom.html || def.html,
    body: custom.body || def.body
  };
};

export const resolvePushTemplate = (cfg, id) => {
  const def = DEFAULT_PUSH_TEMPLATES[id];
  const custom = cfg.pushTemplates[id];
  if (!custom) return def;
  return { title: custom.title || def.title, body: custom.body || def.body };
};

// Read the customer's live points balance (after the increment has been written) so {total_points} is never stale.
export const fetchPointsBalance = async (customerId, fallback = 0) => {
  try {
    const snap = await getDoc(doc(db, 'customers', customerId));
    if (snap.exists()) {
      const v = snap.data().metrics?.pointsBalance;
      if (typeof v === 'number') return v;
    }
  } catch (e) {
    console.warn('Could not read live points balance, using computed value:', e);
  }
  return fallback;
};

// Read the customer's live wallet balance (after the debit/credit has been written) so {balance} is never stale.
export const fetchWalletBalance = async (customerId, fallback = 0) => {
  try {
    const snap = await getDoc(doc(db, 'customers', customerId));
    if (snap.exists()) {
      const v = snap.data().metrics?.walletBalance;
      if (typeof v === 'number') return v;
    }
  } catch (e) {
    console.warn('Could not read live wallet balance, using computed value:', e);
  }
  return fallback;
};
